import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { getPolicyDocument, updatePolicyDocument } from '../../../services/policyService';
import { useAuth } from '../../../context/AuthContext';
import Button from '../../../components/ui/Button';
import TextInput from '../../../components/ui/TextInput';

const Container = styled.div`
  display: flex;
  gap: 24px;
  height: 100%;
`;

const LeftPanel = styled.div`
  flex: 1;
  min-width: 0;
`;

const RightPanel = styled.div`
  width: 340px;
  flex-shrink: 0;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const Label = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #A0A8B4;
  margin-bottom: 4px;
`;

const Value = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #004242;
  margin-bottom: 16px;
`;

const PdfFrame = styled.iframe`
  width: 100%;
  height: 500px;
  border: 1px solid #E0E4E8;
  border-radius: 4px;
`;

const FormRow = styled.div`
  margin-bottom: 16px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
`;

export default function GeneralTab({ documentId }) {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const [doc, setDoc] = useState(null);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', category: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getPolicyDocument(documentId)
      .then((res) => {
        const d = res.data?.data;
        setDoc(d);
        if (d) setFormData({ title: d.title || '', description: d.description || '', category: d.category || '' });
      });
  }, [documentId]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await updatePolicyDocument(documentId, formData);
      setDoc((prev) => ({ ...prev, ...formData }));
      setEditing(false);
    } catch {
      // error handled by interceptor
    } finally {
      setSaving(false);
    }
  }, [documentId, formData]);

  if (!doc) return null;

  return (
    <Container>
      <LeftPanel>
        <Card>
          {doc.fileUrl && (
            <PdfFrame src={doc.fileUrl} title={doc.title} />
          )}
        </Card>
      </LeftPanel>

      <RightPanel>
        <Card>
          {!editing ? (
            <>
              <Label>{t('policy.documentTitle')}</Label>
              <Value>{doc.title}</Value>

              <Label>{t('policy.category')}</Label>
              <Value>{doc.category || '-'}</Value>

              <Label>{t('policy.description')}</Label>
              <Value>{doc.description || '-'}</Value>

              <Label>{t('policy.version')}</Label>
              <Value>{doc.version || '1'}</Value>

              {canWrite('ET') && (
                <Button variant="outline" onClick={() => setEditing(true)}>
                  {t('policy.edit')}
                </Button>
              )}
            </>
          ) : (
            <>
              <FormRow>
                <TextInput
                  label={t('policy.documentTitle')}
                  value={formData.title}
                  onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
                  required
                />
              </FormRow>
              <FormRow>
                <TextInput
                  label={t('policy.category')}
                  value={formData.category}
                  onChange={(e) => setFormData((p) => ({ ...p, category: e.target.value }))}
                  required
                />
              </FormRow>
              <FormRow>
                <TextInput
                  label={t('policy.description')}
                  value={formData.description}
                  onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                  type="textarea"
                  rows={3}
                />
              </FormRow>
              <Footer>
                <Button variant="ghost" onClick={() => setEditing(false)} disabled={saving}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? '...' : t('common.save')}
                </Button>
              </Footer>
            </>
          )}
        </Card>
      </RightPanel>
    </Container>
  );
}
